/* eslint-disable react-refresh/only-export-components --
 * Router definition module: exports the route tree alongside lazy route
 * components. Not a Fast Refresh boundary, so the rule does not apply. */
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { Layout } from './components/Layout'
import Coach from './views/Coach'
import Dashboard from './views/Dashboard'
import Sessions from './views/Sessions'
import { lazy, Suspense } from 'react'
import { NotFound } from './components/errors/NotFound'
import { RouteError } from './components/errors/RouteError'
import { OnCanonicalHost } from './components/OnCanonicalHost'
import { PendingDeletionGate } from './components/PendingDeletionGate'

const Login = lazy(() => import('./views/Login'))
const SignedIn = lazy(() => import('./views/SignedIn'))
const Invite = lazy(() => import('./views/Invite'))
const FirstRun = lazy(() => import('./views/FirstRun'))
const Builder = lazy(() => import('./views/Builder'))
const SessionList = lazy(() => import('./views/SessionList'))
const Journey = lazy(() => import('./views/journey'))
const Profile = lazy(() => import('./views/Profile'))
const Settings = lazy(() => import('./views/Settings'))
const KnowledgeBase = lazy(() => import('./views/KnowledgeBase'))
const CatalogSetup = lazy(() => import('./views/CatalogSetup'))

/**
 * Application router — React Router v7 data mode.
 *
 * Four routes sit outside the Layout shell, for two different reasons.
 * /login, /signed-in (the hop the sign-in service ends at, #360) and /invite/:code
 * are unauthenticated or not yet confirmed and must not see the app chrome
 * at all; /first-run is authenticated but has no profile yet, so the chrome
 * would render empty behind it. Everything else is a child of the shell, and
 * `routing.test.tsx` pins that shape in both directions.
 *
 * Every route is wrapped in OnCanonicalHost (LP-D5, #769): on the root domain an app page moves to
 * app., on app. the sign-in and invite pages move to the root domain, and on the retired host every
 * page moves (`api/canonicalHost.ts` has the rules). A page renders only on the host it belongs to.
 *
 * /coach is a normal Layout child. The coach is a *page in the shell*, not an
 * overlay (Coach v3.dc.html line 23), so it keeps the rail, the mobile bottom
 * bar and DevSessionBadge like everything else. What it does
 * not keep is the CoachDock — it has its own composer, so Layout guards the
 * dock on pathname. It is still not a destination on the rail, which is why
 * the Coach item was removed from Sidebar: you reach it by saying something.
 */
export const router = createBrowserRouter([
  { path: '/login', element: <OnCanonicalHost><Suspense fallback={null}><Login /></Suspense></OnCanonicalHost>, errorElement: <RouteError /> },
  // Where a session cookie has just been set: on the root domain by the sign-in service's callback,
  // which hands the session to app. from here (#769), and on app. by that hand-off, which confirms it
  // and goes in. Outside the shell, because the shell's gate is what it is about to satisfy.
  { path: '/signed-in', element: <OnCanonicalHost><Suspense fallback={null}><SignedIn /></Suspense></OnCanonicalHost>, errorElement: <RouteError /> },
  // Public. An invitee holds no credential of any kind -- they are not yet a
  // user -- so this must not sit inside the shell or behind the guard.
  // /login and this page live on the root domain (LP-D2, LP-D3): opened on app. or on the retired host
  // (an invite link already sent points there), OnCanonicalHost moves them there before a sign-in can
  // start (S-O35, LP-D5).
  { path: '/invite/:code', element: <OnCanonicalHost><Suspense fallback={null}><Invite /></Suspense></OnCanonicalHost>, errorElement: <RouteError /> },
  // Authenticated but deliberately outside the shell: a user in first run has
  // no profile, so the sidebar, the coach dock and every data-fetching widget
  // would render empty behind it.
  // An account pending deletion sees its own screen here too (#365): first run would only meet 403s.
  { path: '/first-run', element: <OnCanonicalHost><PendingDeletionGate><Suspense fallback={null}><FirstRun /></Suspense></PendingDeletionGate></OnCanonicalHost>, errorElement: <RouteError /> },
  {
    path: '/',
    // The shell, and every app page in it, belongs on app.: opened on the root domain or the retired
    // host it moves there before it draws or asks the server for anything (LP-D5).
    element: <OnCanonicalHost><Layout /></OnCanonicalHost>,
    // Attached to the shell rather than to each child, so a throw inside
    // Layout itself is covered too. It replaces the shell when it renders.
    errorElement: <RouteError />,
    children: [
      { index: true, element: <Dashboard /> },
      { path: 'coach', element: <Coach /> },
      // /dashboard was home until 2026-08-06. Kept as a redirect so bookmarks,
      // the iOS app's deep links and anything else pointing at the old URL
      // still land somewhere real.
      { path: 'dashboard', element: <Navigate to="/" replace /> },
      { path: 'sessions', element: <Sessions /> },
      // SB-5 (cycle 007). `/builder` is the session LIST and `/builder/:id`
      // is the editor. The sidebar's "Session Builder" item still points at
      // `/builder` and now lands on the list, which is where a user with six
      // sessions wants to arrive. `Open` on a row is one click and one
      // navigation; before this split nothing could be deep-linked at all,
      // because the editor auto-opened `templates[0]` regardless of the URL.
      {
        path: 'builder',
        children: [
          { index: true, element: <Suspense fallback={null}><SessionList /></Suspense> },
          { path: ':templateId', element: <Suspense fallback={null}><Builder /></Suspense> },
        ],
      },
      { path: 'progress', element: <Navigate to="/journey" replace /> },
      { path: 'body', element: <Navigate to="/journey" replace /> },
      { path: 'journey', element: <Suspense fallback={null}><Journey /></Suspense> },
      { path: 'profile', element: <Suspense fallback={null}><Profile /></Suspense> },
      { path: 'settings', element: <Suspense fallback={null}><Settings /></Suspense> },
      { path: 'knowledge-base', element: <Suspense fallback={null}><KnowledgeBase /></Suspense> },
      // Section A (SB-2). Inside the shell: the user is authenticated and has
      // a profile -- this is equipment setup, not identity setup, and it is
      // re-entered from Settings as often as it is run at first run.
      { path: 'catalog-setup', element: <Suspense fallback={null}><CatalogSetup /></Suspense> },
      // Unmatched URLs, INSIDE the shell (Coinbase's pattern): a user who
      // mistyped an address still has a session and still has the rail, so
      // taking the navigation away would turn a wrong turn into a dead end.
      // Being a child also means it inherits RequireFirstRun, so a signed
      // out visitor lands on /login rather than on a 404 in an empty frame.
      // Must stay LAST: a splat sibling would shadow routes declared after it.
      { path: '*', element: <NotFound /> },
    ],
  },
])
